import { Button, Card, Flex } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { snakeCase } from 'utils/snakeCase';

import StatusComponent from 'components/status/status';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ProfitCapitalizationComponent from '../components/profitCapitalizationComponent';
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';

import NotificationService from 'core/services/notification';
import { StatusColors } from 'utils/statusColors';
import { onCancelCapitalRaise, onGetRaiseCapital } from '../services/raiseCapitals';

import { ProfitCapitalization } from '../models/profitCapitalization';
import TreasurySharesAcquisitionComponent from '../components/treasurySharesAquisitionComponent';
import { TreasurySharesAcquisition } from '../models/treasurySharesAcquisition';
import { PrivateEquityIssuance } from '../models/privateEquityIssuance';
import PrivateEquityIssuanceComponent from '../components/PrivateEquityComponent';
import { dateFormat } from 'utils/dateFormat';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';

const RaiseCapitalsViewPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const { id } = useParams<{ id: string }>();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [raiseCapitals, setRaiseCapitals] = useState<ProfitCapitalization | TreasurySharesAcquisition | PrivateEquityIssuance>();

	const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
	const [isCancelling, setIsCancelling] = useState<boolean>(false);

	useEffect(() => {
		getRaiseCapitalDetails();
	}, []);

	const getRaiseCapitalDetails = async () => {
		try {
			setIsLoading(true);

			const response = await onGetRaiseCapital(Number(id), 1);
			setRaiseCapitals(response);

			setIsLoading(false);
		} catch (error: any) {
			setIsLoading(false);
			const message = error?.response?.data?.message || t('COMMON.FETCH_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);

			navigate('/dashboard/actions/capitals');
		}
	};

	const cancelHandler = async () => {
		try {
			setIsCancelling(true);
			await onCancelCapitalRaise(raiseCapitals?.action.id as number);
			NotificationService.success(
				t('COMMON.SUCCESS'),
				raiseCapitals?.action.type_key === 'ProfitCapitalization'
					? t('ACTIONS.PROFIT_CAPITALIZATION_CANCELLED')
					: raiseCapitals?.action.type_key === 'TreasurySharesAcquisition'
					? t('ACTIONS.TREASURY_SHARES_ACQUISITION_CANCELLED')
					: t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_CANCELLED'),
			);
			setCancelModalOpen(false);
			setIsCancelling(false);
			getRaiseCapitalDetails();
		} catch (error) {
			setIsCancelling(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.CAPITAL_RAISE_NOT_CANCELLED'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<Card>
						<Flex justify="space-between" className="mb-4">
							<div>
								<Flex align="center" gap={20}>
									<h5 className="mb-0 fw-medium">{raiseCapitals?.action.formated_request_number}</h5>
									<StatusComponent
										small
										text={t(`COMMON.${raiseCapitals?.action.status_key.toUpperCase()}`)}
										color={StatusColors[raiseCapitals?.action.status_key || '']}
									/>
								</Flex>
								<p className="text-light mt-1 mb-3">{dateFormat(raiseCapitals?.action.request_date!)}</p>
							</div>

							{raiseCapitals?.action?.status_key !== 'Canceled' && (
								<Button type="primary" danger size="middle" onClick={() => setCancelModalOpen(true)}>
									{t('COMMON.CANCEL')}
								</Button>
							)}
						</Flex>

						<div>
							<h6 className="fw-medium mb-3" style={{ fontSize: '1.15rem' }}>
								{t('ACTIONS.TYPE_OF_RAISE')}
							</h6>
							{raiseCapitals?.action.type_key === 'ProfitCapitalization' && (
								<>
									<h6 className="mb-0">{t('ACTIONS.PROFIT_CAPITALIZATION')}</h6>
									<p className="mb-0 text-light">{t('ACTIONS.PROFIT_CAPITALIZATION_DESC')}</p>
								</>
							)}
							{raiseCapitals?.action.type_key === 'TreasurySharesAcquisition' && (
								<>
									<h6 className="mb-0">{t('ACTIONS.TREASURY_SHARES_ACQUISITION')}</h6>
									<p className="mb-0 text-light">{t('ACTIONS.TREASURY_SHARES_ACQUISITION_DESC')}</p>
								</>
							)}
							{raiseCapitals?.action.type_key === 'PrivateEquityIssuance' && (
								<>
									<h6 className="mb-0">{t('ACTIONS.PRIVATE_EQUITY_ISSUANCE')}</h6>
									<p className="mb-0 text-light">{t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_DESC')}</p>
								</>
							)}
						</div>

						{raiseCapitals?.action.type_key === 'ProfitCapitalization' && (
							<ProfitCapitalizationComponent raiseCapital={raiseCapitals as ProfitCapitalization} onReload={getRaiseCapitalDetails} />
						)}

						{raiseCapitals?.action.type_key === 'TreasurySharesAcquisition' && (
							<TreasurySharesAcquisitionComponent treasurySharesAcquisition={raiseCapitals as TreasurySharesAcquisition} />
						)}

						{raiseCapitals?.action.type_key === 'PrivateEquityIssuance' && (
							<PrivateEquityIssuanceComponent privateEquityIssuance={raiseCapitals as PrivateEquityIssuance} />
						)}
					</Card>
				</>
			)}

			{/* CANCEL MODAL */}
			<ConfirmModalComponent
				open={cancelModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={cancelHandler}
				isLoading={isCancelling}
				onDiscard={() => setCancelModalOpen(false)}
				title={
					raiseCapitals?.action.type_key === 'ProfitCapitalization'
						? t('ACTIONS.CANCEL_PROFIT_CAPITALIZATION')
						: raiseCapitals?.action.type_key === 'TreasurySharesAcquisition'
						? t('ACTIONS.CANCEL_TREASURY_SHARES_ACQUISITION')
						: t('ACTIONS.CANCEL_PRIVATE_EQUITY_ISSUANCE')
				}
				description={
					raiseCapitals?.action.type_key === 'ProfitCapitalization'
						? t('ACTIONS.CONFIRM_CANCEL_PROFIT_CAPITALIZATION')
						: raiseCapitals?.action.type_key === 'TreasurySharesAcquisition'
						? t('ACTIONS.CONFIRM_CANCEL_TREASURY_SHARES_ACQUISITION')
						: t('ACTIONS.CONFIRM_CANCEL_PRIVATE_EQUITY_ISSUANCE')
				}
				descriptionClasses="text-center"
			/>
		</>
	);
};

export default RaiseCapitalsViewPage;
